import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import useGeoCheck from '../../hooks/useGeoCheck'
import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import { api } from '../../utils/api-client'
import closeModal from '../../utils/closeModal'
import { isValidEmail } from '../../utils/common'
import Icon from '../icon'
import { TextInput } from '../register'
import Modal from './modal'
import Spinner from './spinner'

const RequestPasswordResetModal = () => {
  const { t } = useTranslation(['login', 'recoverPassword', 'register', 'common'])
  const { geoCheck } = useGeoCheck()
  const navigate = useNavigate()
  const modal = useModal()
  const [account, setAccount] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState<{
    text: string
    isValid: boolean
  } | null>(null)

  const username = useBoundStore(state => state.user.username)

  const onSend = async () => {
    if (!account)
      return setMessage({
        text: t('username-required', { ns: 'register' }),
        isValid: false,
      })

    if (!isValidEmail(account))
      return setMessage({
        text: t('invalid-email', { ns: 'register' }),
        isValid: false,
      })

    setIsLoading(true)
    const reqBody = {
      type: username ? 'send_reset_password' : 'send_reset_password_public',
      account,
    }
    try {
      const response = await api.post<ApiResponse<string>>(
        '/request.php',
        reqBody,
      )
      const { info, status } = response.data
      setMessage({ text: info, isValid: status === 1 })
    } catch (error) {
      setMessage({
        text: (error as Error).message || t('common:something-went-wrong'),
        isValid: false,
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal id="request-password-reset-modal">
      <button
        type="button"
        className="flex absolute right-[15px] top-5"
        onClick={() => closeModal(modal)}>
        <Icon id="close" width="12" height="12" className="fill-txt" />
      </button>
      <p className="text-[15px] uppercase text-txt text-center mb-[25px]">
        {t('title', { ns: 'recoverPassword' })}
      </p>
      {message?.isValid ? (
        <p className="text-[15px] text-success text-center">{message.text}</p>
      ) : (
        <>
          <p className="text-[15px] text-txt mb-[5px]">
            {t('enter-email-only', { ns: 'recoverPassword' })}
          </p>
          {message && (
            <p className="text-[15px] text-error mb-[10px]">{message.text}</p>
          )}
          <TextInput
            name="account"
            placeholder={t('email', { ns: 'recoverPassword' })}
            value={account}
            onChange={e => {
              setAccount((e.target as HTMLInputElement).value)
              setMessage(null)
            }}
            variant="secondary"
          />
          <div className="flx-ctr" style={{ margin: '20px 0' }}>
            {isLoading ? (
              <Spinner />
            ) : (
              <button
                className="text-center text-[15px] bg-primary-btn-bg border-primary-btn-bg text-primary-btn-txt transition-all duration-[0.2s] ease-[ease-in-out] cursor-pointer font-normal block mx-auto my-0 px-6 py-[7px] rounded-[22px] border-[1.5px] border-solid"
                onClick={onSend}>
                {t('send-request', { ns: 'recoverPassword' })}
              </button>
            )}
          </div>
          <p className="text-[15px] text-center text-txt mb-[5px]">
            {t('opened', { ns: 'recoverPassword' })}{' '}
            <button
              onClick={() => {
                geoCheck(() => navigate({ to: '/register' }))
                closeModal(modal)
              }}
              className="text-highlight underline">
              {t('join-now', { ns: 'recoverPassword' })}
            </button>
          </p>
        </>
      )}
    </Modal>
  )
}
export default NiceModal.create(RequestPasswordResetModal)
